/**
 * B196 L3 — the stop screen's own removal call (F4, spec §4.6, founder
 * ratified 2026-09-17: "B196 refused signup may delete itself: ratified").
 *
 * The single caller this exists for: the age-step stop screen's "Remove
 * this sign-in" control (owned by lane L2, `AgeStep.tsx`). L2 imports this
 * module BY NAME ONLY — the export signature below is the seam and must not
 * drift (`removeRefusedSignup(): Promise<{ ok: boolean; code?: string }>`,
 * no parameters).
 *
 * Posts `POST /api/account/refused-signup-delete` — server-side eligibility
 * (spec §4.6, `api/lib/account/deletionPlan.js`
 * `decideRefusedSignupDeletionEligibility`) decides whether the request
 * succeeds; this helper only carries it through the app's authed fetch and
 * maps the response body to the shape the stop screen renders from.
 *
 * ⛔ No `confirm` body, no query parameters — the endpoint's closed shape
 * (BLOCK-2's allowlist idiom, narrowed to the empty set) refuses ANY key on
 * either channel, so this helper must never add one.
 */

import { authFetch } from './authFetch';
import { getSupabaseClient } from './supabase';

export interface RefusedSignupDeleteResult {
  ok: boolean;
  /** A machine code from the endpoint's refusal shape (`{error, code}`), e.g.
   *  'minor_deletion_pending_flow', 'refused_signup_not_eligible',
   *  'self_only', 'subject_invalid' — or the synthetic `RATE_LIMITED_CODE`
   *  below (the 429 response carries no `code` field of its own). Absent on
   *  success or on a response with no readable body (a network failure never
   *  has one). */
  code?: string;
}

/**
 * B211 — the endpoint's 429 (`Rate limit exceeded`) carries NO `code` field,
 * unlike every other refusal here, so this client assigns its own key to
 * branch on it. Not a server-mirrored spelling; do not add it to
 * `MIRRORED_SERVER_MESSAGES` below.
 */
export const RATE_LIMITED_CODE = 'rate_limited';

/**
 * B211 — the stop screen's own sentences for the two codes this endpoint can
 * actually refuse with post-eligibility. Mirrored BYTE-FOR-BYTE from
 * `api/index.js`'s `handleRefusedSignupDelete` `messages` map — the seam
 * contract test in `refusedAccount.test.ts` reads the real server source and
 * asserts these match; a drift there fails here, not silently.
 */
export const MIRRORED_SERVER_MESSAGES: Readonly<Record<string, string>> = {
  refused_signup_not_eligible: 'This sign-in cannot be removed here. Contact support@tayro.run.',
  minor_deletion_pending_flow:
    'This account is under a guardian’s care. Your guardian can delete it from their Tayro home.',
  // B216-fix1 (UX finding 2) — another door is already removing this same
  // account (the B216 single-flight claim). Mirrored byte-for-byte from
  // `api/index.js`'s `handleRefusedSignupDelete` `messages` map. Without
  // this entry the code fell to the generic "Could not remove this sign-in."
  // line, which reads as a dead end rather than "wait, it's already
  // happening."
  deletion_in_progress: 'This account is already being removed.',
};

/** B211 — bounded 429 sentence. Not a server spelling (see `RATE_LIMITED_CODE`). */
const RATE_LIMITED_MESSAGE = 'Too many tries. Wait a minute and try again.';

/** B211 — the fallback for any other refusal (`self_only`, `subject_invalid`,
 *  a network failure with no code at all, and so on). Mirrors the handler's
 *  own outer-catch default, `api/index.js`'s
 *  `'Could not remove this sign-in.'`. */
const GENERIC_REMOVAL_FAILURE_MESSAGE = 'Could not remove this sign-in.';

/**
 * B211 — the sentence the stop screen renders for a failed removal. Never
 * invents a fourth spelling: the two eligibility refusals are the server's
 * own words, the 429 is the one bounded client sentence, and everything else
 * falls to the same generic line the server's own catch-all uses.
 */
export function refusedSignupRemovalMessage(result: RefusedSignupDeleteResult): string {
  const mirrored = result.code ? MIRRORED_SERVER_MESSAGES[result.code] : undefined;
  if (mirrored) return mirrored;
  if (result.code === RATE_LIMITED_CODE) return RATE_LIMITED_MESSAGE;
  return GENERIC_REMOVAL_FAILURE_MESSAGE;
}

/**
 * Remove the caller's own refused sign-in. Never throws — a network failure,
 * a missing session, or an unreadable response body all resolve to
 * `{ ok: false }` rather than rejecting, so the stop screen never needs a
 * try/catch of its own around this call.
 */
export async function removeRefusedSignup(): Promise<RefusedSignupDeleteResult> {
  try {
    const supabase = await getSupabaseClient();
    const res = await authFetch(supabase, '/api/account/refused-signup-delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
      surface: 'react',
    });
    if (res.ok) return { ok: true };
    // B211 — the 429 body is `{ error: 'Rate limit exceeded' }`, no `code`
    // field; assign the synthetic code before falling into the generic
    // body-parse path below, so a rate limit is never indistinguishable from
    // an unreadable-body failure.
    if (res.status === 429) return { ok: false, code: RATE_LIMITED_CODE };
    try {
      const body: unknown = await res.json();
      const code =
        body && typeof body === 'object' && typeof (body as { code?: unknown }).code === 'string'
          ? (body as { code: string }).code
          : undefined;
      return { ok: false, code };
    } catch {
      return { ok: false };
    }
  } catch {
    return { ok: false };
  }
}
